/**
 * Auth service — Supabase-backed but exposes the same shape as the original
 * Saheeh Firebase auth so the rest of the app doesn't have to change.
 *
 * Why Supabase: while a dedicated Saheeh Firebase project is being set up,
 * we route auth through the BlackLayers Supabase project so users can
 * actually log in and see the UI. Firestore data calls elsewhere in the app
 * will still fail until Firebase is wired up (or until those services are
 * also ported).
 *
 * Exported shape (preserved from the original):
 *   FirebaseAuthService.getInstance()
 *   .signIn(email, password)       → { success, user?, error? }
 *   .signOut()
 *   .getCurrentUser()              → User-like object with .email, .uid, .getIdToken
 *   .isAuthenticated()
 *   .getAuthToken()                → Promise<string | null>
 */

import { supabase } from './supabaseClient';

export interface UserProfile {
  uid: string;
  email: string;
  name: string;
  role: 'admin' | 'staff' | 'manager';
  restaurant_id?: string;
  created_at: string;
  last_login: string;
}

interface AuthUserLike {
  uid: string;
  email: string;
  displayName: string;
  getIdToken: () => Promise<string>;
}

const HARDCODED_ADMINS = [
  'admin@saheehpos.com',
  'admin@saheeh.com',
  'owner@saheehpos.com',
];

function profileFromSupabase(user: any, accessToken: string | undefined): { profile: UserProfile; authUser: AuthUserLike } {
  const email = (user?.email ?? '').toLowerCase();
  const role: UserProfile['role'] =
    HARDCODED_ADMINS.includes(email) ||
    (user?.app_metadata?.role === 'tenant_admin' || user?.app_metadata?.role === 'platform_admin')
      ? 'admin'
      : (user?.app_metadata?.role === 'manager' ? 'manager' : 'staff');
  const profile: UserProfile = {
    uid: user.id,
    email,
    name: user.user_metadata?.full_name || email,
    role,
    restaurant_id: user.app_metadata?.tenant_id,
    created_at: user.created_at ?? new Date().toISOString(),
    last_login: new Date().toISOString(),
  };
  const authUser: AuthUserLike = {
    uid: user.id,
    email,
    displayName: profile.name,
    getIdToken: async () => accessToken ?? '',
  };
  return { profile, authUser };
}

class FirebaseAuthService {
  private static instance: FirebaseAuthService;
  private currentUser: AuthUserLike | null = null;

  private constructor() {
    // eslint-disable-next-line no-console
    console.log('🔥 Firebase Auth Service initialized (Supabase-backed)');

    // Mirror Supabase auth state into our local cache + localStorage so the
    // rest of the app sees the same shape it expects from Firebase.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) {
        const { profile, authUser } = profileFromSupabase(data.session.user, data.session.access_token);
        this.currentUser = authUser;
        localStorage.setItem('saheeh_pos_user', JSON.stringify(profile));
      }
    });

    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        this.currentUser = null;
        localStorage.removeItem('saheeh_pos_session');
        localStorage.removeItem('saheeh_pos_user');
        return;
      }
      const { profile, authUser } = profileFromSupabase(session.user, session.access_token);
      this.currentUser = authUser;
      localStorage.setItem('saheeh_pos_user', JSON.stringify(profile));
    });
  }

  public static getInstance(): FirebaseAuthService {
    if (!FirebaseAuthService.instance) {
      FirebaseAuthService.instance = new FirebaseAuthService();
    }
    return FirebaseAuthService.instance;
  }

  public async signIn(email: string, password: string): Promise<{ success: boolean; user?: UserProfile; error?: string }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // Map Supabase error messages to the simpler set the Saheeh UI expects.
        let msg = error.message || 'Login failed';
        if (msg.toLowerCase().includes('invalid login credentials')) {
          msg = 'Invalid email or password';
        }
        return { success: false, error: msg };
      }
      if (!data.user || !data.session) {
        return { success: false, error: 'No session returned' };
      }

      const { profile, authUser } = profileFromSupabase(data.user, data.session.access_token);
      this.currentUser = authUser;

      const sessionData = {
        user: profile,
        token: data.session.access_token,
        expiresAt: Date.now() + (24 * 60 * 60 * 1000),
      };
      localStorage.setItem('saheeh_pos_session', JSON.stringify(sessionData));
      localStorage.setItem('saheeh_pos_user', JSON.stringify(profile));

      // eslint-disable-next-line no-console
      console.log('✅ Login successful:', profile.email, '(role:', profile.role + ')');

      // Shift-open (FRAUD PREVENTION #4) is a Firebase Cloud Function call —
      // not available in Supabase-backed mode, so we skip it silently. Cashier
      // workflows that rely on the shift document will degrade gracefully.

      return { success: true, user: profile };
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('❌ Login failed:', err);
      return { success: false, error: err?.message ?? 'Login failed' };
    }
  }

  public async signOut(): Promise<void> {
    try {
      await supabase.auth.signOut();
      this.currentUser = null;
      localStorage.removeItem('saheeh_pos_session');
      localStorage.removeItem('saheeh_pos_user');
      localStorage.removeItem('saheeh_pos_opening_cash_asked');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('❌ Sign out failed:', e);
    }
  }

  public getCurrentUser(): AuthUserLike | null {
    return this.currentUser;
  }

  public isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  public async getAuthToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }
}

export default FirebaseAuthService;
